import { toast } from '@askable/ui/core/sonner';
import { useSetAtom } from 'jotai';
import { CheckIcon } from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { downloadQueueAtom } from '../data/downloadQueue.atom';
import { pollingRegistryAtom } from '../data/pollingRegistry.atom';
import { setIsExecutingDownload } from '../utils/downloadWindowState';

import type { PollingConfig } from '../data/pollingRegistry.atom';
import type { DownloadItem, DownloadQueueState } from '../types';

const downloadFilename = (downloadUrl: string) => {
  const defaultFilename = 'download';
  try {
    const url = new URL(downloadUrl);
    const attachmentFilename = url.searchParams
      ?.get('response-content-disposition')
      ?.split('filename=')[1]
      ?.replace(/^"|"$/g, '');
    if (attachmentFilename) {
      return attachmentFilename;
    }
    const pathnameFilename = url.pathname?.split('/')?.pop();
    if (pathnameFilename) {
      return pathnameFilename;
    }
    return defaultFilename;
  } catch {
    return defaultFilename;
  }
};

/**
 * Hook for executing browser downloads and managing post-download cleanup
 */
export const useDownloadExecutor = () => {
  const setDownloadQueue = useSetAtom(downloadQueueAtom);
  const setPollingRegistry = useSetAtom(pollingRegistryAtom);
  const { t } = useTranslation();

  const executeDownload = useCallback(
    (download: DownloadItem) => {
      if (!download.downloadUrl) {
        throw new Error('Download URL is required');
      }

      // Update status to downloading
      setDownloadQueue((prev: DownloadQueueState) => ({
        ...prev,
        downloads: {
          ...prev.downloads,
          [download.id]: {
            ...prev.downloads[download.id],
            status: 'downloading',
          },
        },
      }));

      // Create temporary anchor element and trigger download
      const anchor = document.createElement('a');
      anchor.href = download.downloadUrl;
      // Prefer a caller-supplied filename (e.g. the task-recording name builder);
      // fall back to deriving it from the signed URL.
      anchor.download = download.metadata?.filename || downloadFilename(download.downloadUrl);
      anchor.style.display = 'none';
      document.body.appendChild(anchor);

      // Set flag to allow navigation during download execution
      setIsExecutingDownload(true);
      anchor.click();
      setIsExecutingDownload(false);

      document.body.removeChild(anchor);

      // Update status to completed
      setDownloadQueue((prev: DownloadQueueState) => {
        const newActivePollingIds = new Set(prev.activePollingIds);
        newActivePollingIds.delete(download.id);

        return {
          downloads: {
            ...prev.downloads,
            [download.id]: {
              ...prev.downloads[download.id],
              status: 'completed',
              completedAt: Date.now(),
            },
          },
          activePollingIds: newActivePollingIds,
        };
      });

      // Clean up polling registry
      setPollingRegistry((prev: Record<string, PollingConfig>) => {
        const { [download.id]: _, ...newRegistry } = prev;
        return newRegistry;
      });

      // Dismiss loading toast and show success — unless this download is silent
      // (a batch caller owns the consolidated toast).
      const toastId = download.metadata?.toastId;
      if (toastId) {
        toast.dismiss(toastId);
      }
      if (!download.metadata?.silent) {
        toast.success(t('components.downloadManager.successToast'), {
          icon: <CheckIcon className="h-4 w-4 text-success" />,
        });
      }
    },
    [setDownloadQueue, setPollingRegistry, t],
  );

  return { executeDownload };
};
